import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Settings, Shield } from "lucide-react";

interface DashboardStats {
  totalConversations: number;
  totalUsers: number;
  monthlyUsage: number;
  growthRate: number;
}

interface OverviewTabProps {
  dashboardStats: DashboardStats | null;
  dashboardLoading: boolean;
  dashboardError: string | null;
  onNavigateToTab: (tabValue: string) => void;
}

const StatCard = ({
  title,
  value,
  icon: Icon,
  change,
  color = "blue",
}: {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  change?: string;
  color?: "blue" | "green" | "purple" | "orange";
}) => {
  const colorClasses = {
    blue: "text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20",
    green:
      "text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20",
    purple:
      "text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/20",
    orange:
      "text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20",
  };

  return (
    <Card className=" rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border ">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {title}
            </p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {value}
            </p>
            {change && (
              <p className="text-sm font-medium text-green-600 dark:text-green-400 mt-1">
                {change}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const OverviewTab: React.FC<OverviewTabProps> = ({
  dashboardStats,
  dashboardLoading,
  dashboardError,
  onNavigateToTab,
}) => {
  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {dashboardLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </CardContent>
            </Card>
          ))
        ) : dashboardError ? (
          <div className="col-span-full">
            <Card className="border-red-200 dark:border-red-800">
              <CardContent className="p-6 text-center">
                <p className="text-red-600 dark:text-red-400">
                  Erreur: {dashboardError}
                </p>
              </CardContent>
            </Card>
          </div>
        ) : dashboardStats ? (
          <>
            <StatCard
              title="Utilisateurs"
              value={dashboardStats.totalUsers}
              icon={Users}
              color="orange"
            />
            <StatCard
              title="Statistique 1"
              value={dashboardStats.totalUsers || 0}
              icon={Settings}
              change={`+${dashboardStats.growthRate}%`}
              color="blue"
            />
            <StatCard
              title="Statistique 2"
              value={dashboardStats.monthlyUsage || 0}
              icon={Shield}
              color="green"
            />
            <StatCard
              title="Statistique 3"
              value={dashboardStats.totalConversations}
              icon={Settings}
              color="purple"
            />
          </>
        ) : null}
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Activity */}
        <Card className="lg:col-span-2  rounded-xl shadow-lg border ">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
              Activité Récente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Aucune activité récente
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Les activités du système apparaîtront ici
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className=" rounded-xl shadow-lg border ">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
              Actions Rapides
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start opacity-90 hover:opacity-100"
              onClick={() => onNavigateToTab("analytics")}
            >
              <Settings className="h-4 w-4 mr-2" />
              Voir les Analyses
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start opacity-90 hover:opacity-100"
              onClick={() => onNavigateToTab("users")}
            >
              <Users className="h-4 w-4 mr-2" />
              Gérer les Utilisateurs
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
